// Money Back Australia — app design tokens. Mirrors the marketing brand system
// (apps/marketing/src/styles/brand.css) so web + app stay in lockstep. Brand
// direction: "Plucky Challenger / Honey" with the "mb" coin.
//
// Two layers live here: the raw brand palette (cream/ink/honey/ocean/sand) and
// a set of legacy semantic aliases (brand/surface/textPrimary/…) that older
// screens still import. New screens should prefer the raw tokens.

import { Platform, type TextStyle } from "react-native";

const cream = "#FFFBF3";
const card = "#FFFFFF";
const ink = "#221C15";
const inkSoft = "#473C30";
const muted = "rgba(34,28,21,0.68)"; // ≈4.6:1 on cream — AA for small text (was 0.58/4.1:1)
const honey = "#EC9A12";
const honeyDeep = "#8F5D00"; // ~5.4:1 on cream — AA for small text (links / eyebrows / prices); was #B9760A (~3.6:1). Darkening also lifts contrast on its fill uses (progress, dots).
const honeyTint = "#FCEFD6";
const ocean = "#15697D";
const oceanDeep = "#0F5263";
const oceanTint = "#DCEBEE";
const sand = "#F4ECDD";
const line = "rgba(34,28,21,0.12)";
const lineStrong = "rgba(34,28,21,0.24)";
const danger = "#B23A2E"; // warm brick red — errors only (not a brand colour)

export const colors = {
  // raw brand palette
  cream,
  card,
  ink,
  inkSoft,
  muted,
  honey,
  honeyDeep,
  honeyTint,
  ocean,
  oceanDeep,
  oceanTint,
  sand,
  line,
  lineStrong,
  danger,

  // legacy semantic aliases → brand (keep older screens on-brand)
  background: cream,
  surface: card,
  border: line,
  textPrimary: ink,
  textMuted: inkSoft,
  textFaint: muted,
  brand: honey,
  brandAccent: honeyDeep,
  positive: ocean,
  warn: honeyDeep,
} as const;

// Font family names match the keys loaded via useFonts (see src/fonts.ts).
export const fonts = {
  display: "BricolageGrotesque_700Bold", // headings
  displayHeavy: "BricolageGrotesque_800ExtraBold", // wordmark, hero figures
  body: "HankenGrotesk_400Regular",
  bodyMedium: "HankenGrotesk_500Medium",
  bodySemibold: "HankenGrotesk_600SemiBold",
  bodyBold: "HankenGrotesk_700Bold",
  mono: "SpaceMono_400Regular",
  monoBold: "SpaceMono_700Bold",
} as const;

export const space = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  xxl: 32,
} as const;

export const radius = {
  sm: 8,
  md: 12,
  lg: 16,
  xl: 20,
  pill: 999,
} as const;

/** Tabular figures for all dollar amounts (matches the web `tabular-nums`). */
export const tabular = { fontVariant: ["tabular-nums" as const] };

/** Web only: kill the browser's default focus ring on text inputs. Our inputs
 * draw their own focus affordance via the container border, so the native blue
 * box just reads as a visual bug (it sits inset within our framed wrapper). RN
 * ignores `outlineStyle`; on native this spreads to nothing. */
export const webNoOutline: TextStyle =
  Platform.OS === "web"
    ? ({ outlineStyle: "none" } as unknown as TextStyle)
    : {};

/** Format a whole-dollar amount as AUD with thousands separators. */
export function aud(amount: number): string {
  return `A$${Math.round(amount).toLocaleString("en-AU")}`;
}
